import { validateCatalogue, recommend } from './size24-size-recommender-core.js';
const originalNumber = value => Number.isFinite(value) ? String(Math.round(value * 1000000) / 1000000) : '';

export async function loadProductData(productUrl, productId, signal, fetcher = fetch) {
  const url = new URL(productUrl, window.location.origin);
  if (url.origin !== window.location.origin || !url.pathname.startsWith('/products/')) throw Error('Unexpected product URL');
  url.search = ''; url.searchParams.set('view', 'size24-cns-quickorder');
  let merged = null, complete = false; const visited = new Set();
  for (let page = 0; page < 5; page++) {
    if (visited.has(url.href)) throw Error('Repeated page'); visited.add(url.href);
    const response = await fetcher(url.href, { credentials: 'same-origin', cache: 'no-store', signal });
    if (!response.ok) throw Error('Product read failed');
    const part = await response.json();
    if (String(part.product_id) !== productId || !Array.isArray(part.variants) || !Number.isSafeInteger(part.variant_count) || part.variant_count > 250) throw Error('Product mismatch');
    if (!merged) merged = { ...part, variants: [...part.variants] };
    else {
      if (part.variant_count !== merged.variant_count || JSON.stringify(part.options) !== JSON.stringify(merged.options) || part.special_purchase !== merged.special_purchase) throw Error('Product changed');
      merged.variants.push(...part.variants);
    }
    if (part.next_page == null) { complete = true; break; }
    if (!Number.isSafeInteger(part.next_page) || part.next_page <= 0 || !/^page(?:_[a-zA-Z0-9]+)?$/.test(part.page_param)) throw Error('Invalid next page');
    url.searchParams.set(part.page_param, String(part.next_page));
  }
  if (!complete) throw Error('Incomplete pagination');
  if (merged?.special_purchase !== false) throw Error('Special purchase needs separate guidance');
  return validateCatalogue(merged);
}

export class SizeRecommender extends HTMLElement {
  connectedCallback() {
    if (this.controller || !this.querySelector('[data-sr-messages]')) return;
    this.controller = new AbortController(); const options = { signal: this.controller.signal };
    this.copy = JSON.parse(this.querySelector('[data-sr-messages]').textContent);
    this.helper = this.closest('s24-size-helper'); this.card = this.closest('s24-cns-quickorder');
    this.measure = this.querySelector('[data-sr-measure]'); this.units = this.querySelector('[data-sr-units]');
    this.status = this.querySelector('[data-sr-status]'); this.result = this.querySelector('[data-sr-result]');
    this.reset();
    this.helper?.addEventListener('s24-size-help-open', () => this.load(), options);
    this.helper?.addEventListener('s24-size-help-close', () => this.reset(), options);
    this.closest('details')?.addEventListener('toggle', event => { if (event.target.open) this.load(); else this.reset(); }, options);
    this.measure.addEventListener('input', () => {
      const value = this.measure.valueAsNumber;
      this.measurementIn = Number.isFinite(value) && value > 0 ? (this.units.value === 'cm' ? value / 2.54 : value) : null;
      this.invalidate();
    }, options);
    this.measure.addEventListener('keydown', event => { if (event.key === 'Enter') { event.preventDefault(); this.calculate(); } }, options);
    this.units.addEventListener('change', () => {
      this.measure.value = this.measurementIn === null ? '' : originalNumber(this.units.value === 'cm' ? this.measurementIn * 2.54 : this.measurementIn);
      this.querySelector('[data-sr-unit]').textContent = this.units.value;
      this.invalidate();
    }, options);
    this.querySelector('[data-sr-calculate]').addEventListener('click', () => this.calculate(), options);
    this.querySelector('[data-sr-retry]').addEventListener('click', () => this.load(), options);
    this.card?.addEventListener('change', event => {
      if (this.applying || this.contains(event.target)) return;
      this.syncNativeSelection(); this.invalidate();
    }, options);
    if (this.closest('dialog')?.open || this.closest('details')?.open) this.load();
  }
  disconnectedCallback() { this.controller?.abort(); this.controller = null; this.loading = null; this.data = null; this.measurementIn = null; if (this.measure) this.measure.value = ''; this.result?.replaceChildren(); this.lastResult = null; }
  reset() {
    this.measurementIn = null; this.measure.value = ''; this.units.value = 'in';
    this.querySelector('[data-sr-unit]').textContent = 'in'; this.invalidate();
  }
  invalidate() { this.result.replaceChildren(); this.lastResult = null; if (this.data) this.status.textContent = ''; }
  async load() {
    if (this.data) { this.syncNativeSelection(); return; }
    if (this.loading) return this.loading;
    const signal = this.controller.signal;
    this.status.textContent = this.copy.loading; this.querySelector('[data-sr-retry]').hidden = true;
    this.loading = (async () => {
      try {
        let data;
        if (this.card && typeof this.card.load === 'function') {
          await this.card.load();
          if (this.card.data && this.card.data.special_purchase === false) data = validateCatalogue(this.card.data);
        }
        if (!data) data = await loadProductData(this.dataset.productUrl, this.dataset.productId, signal);
        if (signal.aborted || this.controller?.signal !== signal || !this.isConnected) return;
        if (data.product_id !== this.dataset.productId) throw Error('Different product');
        this.data = data; this.buildOptions(); this.syncNativeSelection();
        this.querySelector('[data-sr-controls]').hidden = false; this.status.textContent = '';
      } catch (error) {
        if (signal.aborted) return;
        this.status.textContent = this.copy.load_error; this.querySelector('[data-sr-retry]').hidden = false;
        this.querySelector('[data-sr-controls]').hidden = true;
      } finally { if (!signal.aborted) this.loading = null; }
    })();
    return this.loading;
  }
  buildOptions() {
    const box = this.querySelector('[data-sr-options]'); box.replaceChildren(); this.selects = new Map();
    this.data.options.forEach((option, index) => {
      if (index === this.data.sizeIndex) return;
      const label = document.createElement('label'), select = document.createElement('select');
      label.append(document.createTextNode(option.name));
      select.setAttribute('form', 's24sr-no-form-' + this.dataset.instance);
      const blank = document.createElement('option'); blank.value = ''; blank.textContent = this.copy.choose + ' ' + option.name; select.append(blank);
      for (const value of option.values) { const item = document.createElement('option'); item.value = value; item.textContent = value; select.append(item); }
      if (option.values.length === 1) select.value = option.values[0];
      select.addEventListener('change', () => this.invalidate(), { signal: this.controller.signal });
      label.append(select); box.append(label); this.selects.set(index, select);
    });
  }
  syncNativeSelection() {
    if (!this.data || !this.selects) return;
    let values = this.card?.selectedValues?.();
    if (!values && window.location.pathname === new URL(this.dataset.productUrl, window.location.origin).pathname) {
      const id = new URL(window.location.href).searchParams.get('variant');
      values = this.data.variants.find(variant => variant.id === id)?.options;
    }
    if (values) for (const [index, select] of this.selects) select.value = this.data.options[index].values.includes(values[index]) ? values[index] : (this.data.options[index].values.length === 1 ? this.data.options[index].values[0] : '');
  }
  selection() { return this.data.options.map((option, index) => index === this.data.sizeIndex ? '' : this.selects.get(index)?.value || ''); }
  compute() { if (!this.data) return { status: 'invalid-data', reason: 'not-loaded', choices: [], alternatives: [] }; return recommend(this.data, { policy: this.dataset.policy, measurement: this.measurementIn, units: 'in', selected: this.selection() }); }
  calculate() {
    if (!this.data) { this.load(); return; }
    this.result.replaceChildren(); const answer = this.compute(); this.lastResult = answer;
    const statusKey = { 'confirmation-needed': 'confirm', 'invalid-data': 'load_error', 'invalid-measurement': 'invalid_measurement', 'choose-options': 'missing_options', 'guidance-only': 'unsupported', 'no-match': answer.reason?.startsWith('above-largest') ? 'above_max' : 'no_match' }[answer.status];
    this.status.textContent = statusKey ? this.copy[statusKey] : '';
    if (answer.choices.length) {
      const note = document.createElement('p'); note.className = 's24sh__note'; note.textContent = this.dataset.policy === 'chest_even' ? this.copy.fit_note : (this.dataset.policy === 'length_only' ? this.copy.waist_note : this.copy.waist_choices); this.result.append(note);
    }
    for (const choice of answer.choices) {
      const box = document.createElement('div'); box.className = 's24sr__choice';
      const title = document.createElement('h3'); title.textContent = this.copy[choice.kind === 'closer' ? 'closer' : choice.kind === 'looser' ? 'looser' : 'length'] + ': ' + choice.label; box.append(title);
      const detail = document.createElement('p'); detail.textContent = this.dataset.policy === 'chest_even' ? this.copy.room + ': ' + originalNumber(choice.difference) + ' ' + this.copy.unit_in : (choice.waist === undefined ? this.copy.waist_note : this.copy.waist + ': ' + originalNumber(choice.waist) + ' ' + this.copy.unit_in); box.append(detail);
      if (!choice.available) { const unavailable = document.createElement('p'); unavailable.textContent = this.copy.unavailable; box.append(unavailable); }
      const use = document.createElement('button'); use.type = 'button'; use.textContent = this.copy.use + ' ' + choice.label; use.disabled = !choice.available;
      use.addEventListener('click', () => this.use(choice.variantId), { signal: this.controller.signal }); box.append(use); this.result.append(box);
    }
    if (answer.alternatives?.length) { const alternatives = document.createElement('p'); alternatives.textContent = this.copy.size + ': ' + answer.alternatives.map(choice => choice.label).join(', ') + '. ' + this.copy.confirm; this.result.append(alternatives); }
  }
  async use(variantId) {
    const current = this.compute(); const choice = current.choices.find(item => item.variantId === variantId && item.available);
    const variant = this.data?.variants.find(item => item.id === variantId);
    if (!choice || !variant) { this.invalidate(); this.status.textContent = this.copy.selection_error; return; }
    if (this.card) {
      const live = this.card.data?.variants.find(item => item.id === variantId);
      if (this.card.busy || !live?.available || JSON.stringify(live.options) !== JSON.stringify(variant.options) || !this.card.selects?.length) { this.invalidate(); this.status.textContent = this.copy.selection_error; return; }
      this.applying = true;
      try {
        for (const [index, group] of this.card.selects.entries()) {
          if (group.value && (index === this.data.sizeIndex || group.value !== variant.options[index])) group.clearButton?.click();
        }
        const order = this.data.options.map((_, index) => index).filter(index => index !== this.data.sizeIndex).concat(this.data.sizeIndex);
        for (const index of order) {
          const radio = this.card.selects[index]?.options.find(input => input.value === variant.options[index]);
          if (!radio || radio.disabled) throw Error('Native option unavailable');
          if (!radio.checked) radio.click();
        }
        if (this.card.variantInput?.value !== variantId || this.card.variantInput.disabled) throw Error('Variant not selected');
        this.helper.querySelector('dialog')?.close(); this.reset(); this.status.textContent = this.copy.selected;
      } catch { this.invalidate(); this.status.textContent = this.copy.selection_error; }
      finally { this.applying = false; }
    } else {
      const product = new URL(this.dataset.productUrl, window.location.origin), url = new URL(window.location.href);
      if (product.origin !== url.origin || product.pathname !== url.pathname) { this.status.textContent = this.copy.selection_error; return; }
      url.searchParams.set('variant', variantId); window.location.assign(url.href);
    }
  }
}
if (!customElements.get('s24-size-recommender')) customElements.define('s24-size-recommender', SizeRecommender);
